import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Shimmer } from '../components/shimmer';
import { useListBookingsQuery } from '../store/bookingsApi';
import { buildItdoseReportPdfUrl, logReportView } from '../utils/fetchItdoseReports';
import { useAuth } from '../context/AuthContext';
import StateCard from '../components/common/StateCard';
import { ROUTES, buildRoute } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';

// Account — built to the v5 reference.
//
// The reference has ten sections. Five of them have no data behind them
// at all — there is no endpoint for saved addresses, family profiles,
// recall/"due soon" dates, repeat-test schedules or WhatsApp message
// preferences — so they are not rendered rather than shown as empty
// furniture. All five are listed in API-NOTES.
//
// Built here, each on real data: the profile header, the next visit,
// recent reports, past orders, and the family list. "Family" reads the
// members added during booking, which live in localStorage under
// `trustlab_members` until the family-profiles endpoint exists; the
// booking flow writes the same key.

const MEMBERS_KEY = 'trustlab_members';

const readMembers = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(MEMBERS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const ACTIVE = ['confirmed', 'assigned', 'enroute'];

const STATUS_TONE = {
  confirmed: ['var(--tl-mint)', 'var(--tl-teal-d)'],
  assigned: ['var(--tl-mint)', 'var(--tl-teal-d)'],
  enroute: ['var(--tl-yellow)', 'var(--tl-g9)'],
  collected: ['var(--tl-mint)', 'var(--tl-teal-d)'],
  reported: ['var(--tl-teal)', '#FFFFFF'],
  cancelled: ['#F4E3E2', '#B23B37'],
  awaiting_payment: ['#FFF4CC', '#8A5A00'],
};

const STATUS_LABEL = {
  confirmed: 'Confirmed',
  assigned: 'Trustian assigned',
  enroute: 'On the way',
  collected: 'Collected',
  reported: 'Report ready',
  cancelled: 'Cancelled',
  awaiting_payment: 'Awaiting payment',
};

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
};

const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const sectionHead = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'end',
  justifyContent: 'space-between',
  gap: 1.5,
};

const h2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(24px, 3vw, 34px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const linkSx = {
  fontFamily: UI,
  fontSize: 15,
  fontWeight: 800,
  color: 'var(--tl-teal-d)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--tl-g9)' },
};

const Pill = ({ status }) => {
  const [bg, fg] = STATUS_TONE[status] || ['var(--tl-mint)', 'var(--tl-teal-d)'];
  return (
    <Box
      component="span"
      sx={{
        px: '10px', py: '5px', borderRadius: '999px', bgcolor: bg, color: fg,
        fontFamily: UI, fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
      }}
    >
      {STATUS_LABEL[status] || status}
    </Box>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { data, isLoading } = useListBookingsQuery(undefined, { skip: !isAuthenticated });
  const [members] = useState(readMembers);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?next=${encodeURIComponent(ROUTES.DASHBOARD)}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const bookings = useMemo(() => data?.bookings || [], [data]);

  // Soonest upcoming visit, by slot date.
  const nextVisit = useMemo(() => {
    const open = bookings
      .filter((b) => ACTIVE.includes(b.status))
      .sort((a, b) => String(a.schedule?.date || '').localeCompare(String(b.schedule?.date || '')));
    return open[0] || null;
  }, [bookings]);

  // A booking becomes a report the moment IT Dose stamps a visit id —
  // that is when the PDF is downloadable.
  const reports = useMemo(
    () => bookings.filter((b) => b?.itdose?.visit_id).slice(0, 4),
    [bookings],
  );

  const orders = useMemo(() => bookings.slice(0, 6), [bookings]);

  const openReport = (b) => {
    logReportView({ visitId: b.itdose.visit_id, source: 'account' });
    window.open(buildItdoseReportPdfUrl({ visitId: b.itdose.visit_id }), '_blank', 'noopener,noreferrer');
  };

  const itemLine = (b) => {
    const names = (b.items || []).map((i) => i.name);
    if (!names.length) return '';
    return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1} more`;
  };

  if (!isAuthenticated) return null;

  // Mirrors the real stack — header band, next-visit band, then two
  // cards — so nothing jumps when the bookings land. The old
  // DashboardShimmer drew the pre-v5 KPI grid, which this page no
  // longer has.
  if (isLoading) {
    return (
      <Box
        component="main"
        aria-label="Loading your account"
        sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 2, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Shimmer width="100%" height={120} rounded={28} />
        <Shimmer width="100%" height={148} rounded={28} />
        <Shimmer width="100%" height={240} rounded={28} />
        <Shimmer width="100%" height={300} rounded={28} />
      </Box>
    );
  }

  const firstName = (user?.name || 'there').split(' ')[0];

  return (
    <Box component="main" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 2, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* ── profile header ───────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          justifyContent: 'space-between', gap: 2,
          p: 'clamp(20px, 3.4vw, 32px)', borderRadius: '28px',
          bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              display: 'grid', placeItems: 'center', width: 56, height: 56, flexShrink: 0,
              borderRadius: '50%', bgcolor: 'var(--tl-teal-l)', color: 'var(--tl-g9)',
              fontFamily: DISPLAY, fontSize: 22, fontWeight: 700,
            }}
          >
            {(user?.name || '?').trim().charAt(0).toUpperCase()}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{ m: 0, fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(24px, 3vw, 34px)', lineHeight: 1.05, color: '#FFFFFF' }}
            >
              {`Good to see you, ${firstName}.`}
            </Typography>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-line-2)' }}>
              {[user?.phone, user?.uhid ? `UHID ${user.uhid}` : null].filter(Boolean).join(' · ')}
            </Box>
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => { logout(); navigate(ROUTES.HOME); }}
          sx={{
            cursor: 'pointer', p: '11px 18px', borderRadius: '999px',
            border: '1.5px solid #2E5A52', bgcolor: 'transparent', color: '#FFFFFF',
            fontFamily: UI, fontSize: 14, fontWeight: 800,
            '&:hover': { borderColor: 'var(--tl-teal-l)' },
          }}
        >
          Sign out
        </Box>
      </Box>

      {/* ── next visit ───────────────────────────────────────────────── */}
      {nextVisit ? (
        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            justifyContent: 'space-between', gap: 2,
            p: 'clamp(20px, 3.4vw, 32px)', borderRadius: '28px',
            bgcolor: 'var(--tl-teal)', color: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Box
              component="span"
              sx={{ fontFamily: UI, fontSize: 13, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--tl-g9)' }}
            >
              Next visit
            </Box>
            <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(22px, 2.6vw, 30px)', lineHeight: 1.1 }}>
              {[formatDate(nextVisit.schedule?.date), nextVisit.schedule?.slot].filter(Boolean).join(', ') || 'Slot to be confirmed'}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-teal-50)' }}>
              {[itemLine(nextVisit), nextVisit.patient?.name].filter(Boolean).join(' · ')}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { l: 'Track', to: `${ROUTES.TRACK}?id=${encodeURIComponent(nextVisit.booking_id)}`, dark: true },
              { l: 'Reschedule', to: buildRoute(ROUTES.CANCEL_BOOKING, { id: nextVisit.booking_id }), dark: false },
            ].map((a) => (
              <Box
                key={a.l}
                component={Link}
                to={a.to}
                sx={{
                  p: '13px 20px', borderRadius: '16px',
                  bgcolor: a.dark ? 'var(--tl-g9)' : '#FFFFFF',
                  color: 'var(--tl-g9)',
                  ...(a.dark ? { color: '#FFFFFF' } : null),
                  fontFamily: UI, fontSize: 15, fontWeight: 800, textDecoration: 'none',
                  '&:hover': { color: a.dark ? '#FFFFFF' : 'var(--tl-g9)', filter: 'brightness(1.06)' },
                }}
              >
                {a.l}
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ ...card, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800 }}>No visit booked</Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted-2)' }}>
              Book a home collection and we&rsquo;ll be there in 60 minutes.
            </Box>
          </Box>
          <Box
            component={Link}
            to={ROUTES.BOOKING}
            sx={{
              p: '13px 20px', borderRadius: '16px', bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
              fontFamily: UI, fontSize: 15, fontWeight: 800, textDecoration: 'none',
            }}
          >
            Book a test
          </Box>
        </Box>
      )}

      {/* ── recent reports ───────────────────────────────────────────── */}
      <Box component="section" sx={card}>
        <Box sx={sectionHead}>
          <Typography component="h2" sx={h2}>Recent reports</Typography>
          <Box component={Link} to={ROUTES.REPORTS} sx={linkSx}>All reports →</Box>
        </Box>
        {reports.length === 0 ? (
          <StateCard
            icon="0"
            tone="neutral"
            title="No reports yet"
            body="Once your first test is done, your doctor-verified reports will appear here."
            actions={[{ label: 'Book a test', to: ROUTES.BOOKING }]}
            sx={{ p: 0, bgcolor: 'transparent' }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {reports.map((b) => (
              <Box
                key={b.booking_id}
                sx={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                  justifyContent: 'space-between', gap: 1.5,
                  py: 1.75, borderTop: '1px solid #EEF3F1',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>{itemLine(b)}</Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                    {[formatDate(b.schedule?.date), b.patient?.name].filter(Boolean).join(' · ')}
                  </Box>
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => openReport(b)}
                  sx={{
                    border: 0, cursor: 'pointer', borderRadius: '999px', p: '10px 16px',
                    bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)',
                    fontFamily: UI, fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap',
                    transition: `background-color .25s ${EASE}`,
                    '&:hover': { bgcolor: 'var(--tl-mint-2)' },
                  }}
                >
                  Download PDF
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ── family ───────────────────────────────────────────────────── */}
      {members.length > 0 && (
        <Box component="section" sx={card}>
          <Box sx={sectionHead}>
            <Typography component="h2" sx={h2}>Family</Typography>
            <Box component={Link} to={ROUTES.BOOKING} sx={linkSx}>Add a member →</Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: 1.25 }}>
            {members.map((m) => (
              <Box
                key={m.id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 2, borderRadius: '20px', bgcolor: 'var(--tl-mint)',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'grid', placeItems: 'center', width: 40, height: 40, flexShrink: 0,
                    borderRadius: '50%', bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
                    fontFamily: UI, fontSize: 15, fontWeight: 800,
                  }}
                >
                  {(m.name || '?').trim().charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800 }}>{m.name}</Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                    {[m.relation, m.age].filter(Boolean).join(', ') || 'Family member'}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── orders ───────────────────────────────────────────────────── */}
      <Box component="section" sx={card}>
        <Box sx={sectionHead}>
          <Typography component="h2" sx={h2}>Orders</Typography>
          <Box component={Link} to={ROUTES.BOOKING} sx={linkSx}>Book again →</Box>
        </Box>
        {orders.length === 0 ? (
          <StateCard
            icon="0"
            tone="neutral"
            title="No bookings yet"
            body="Your orders and their status will show up here once you book."
            actions={[{ label: 'Book a test', to: ROUTES.BOOKING }]}
            sx={{ p: 0, bgcolor: 'transparent' }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {orders.map((b) => (
              <Box
                key={b.booking_id}
                component={Link}
                to={`${ROUTES.TRACK}?id=${encodeURIComponent(b.booking_id)}`}
                sx={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                  justifyContent: 'space-between', gap: 1.5,
                  py: 1.75, borderTop: '1px solid #EEF3F1',
                  textDecoration: 'none', color: 'var(--tl-g9)',
                  '&:hover': { color: 'var(--tl-g9)', '& .tl-order-name': { color: 'var(--tl-teal-d)' } },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                  <Box className="tl-order-name" component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>
                    {itemLine(b)}
                  </Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                    {`#${b.booking_id}${b.schedule?.date ? ` · ${formatDate(b.schedule.date)}` : ''}`}
                  </Box>
                </Box>
                <Pill status={b.status} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
